import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Learner } from '@queries/generated';
import { LearnersService } from '@services/learners.service';
import { OrganizationsService } from '@services/organizations.service';
import { Fade } from '@util/animations';
import { IWizard } from '@util/wizard';
import { EMPTY, noop, of } from 'rxjs';
import { catchError, concatAll, map } from 'rxjs/operators';

import { BaseWizard } from '../base-wizard';
import { FormActionsComponent } from '../form-actions/form-actions.component';
import { SidebarToggleComponent } from '../sidebar-toggle/sidebar-toggle.component';
import { IndexedLearnerAttributes } from './add-learners-step/add-learners-step.component';

export interface ImportLearnerAttributes extends IndexedLearnerAttributes {
  organizationIds: string[];
}

enum AddLearnersStepsEnum {
  Add,
  Confirm,
  Import
}

interface AddLearnersWizard extends IWizard<AddLearnersStepsEnum> {
  imported: Learner[];
  learners: ImportLearnerAttributes[];
  organizationIds: string[];
}

@Component({
  selector: 'vnm-add-learners-wizard',
  templateUrl: './add-learners-wizard.component.html',
  styleUrls: ['./add-learners-wizard.component.scss'],
  animations: [Fade]
})
export class AddLearnersWizardComponent extends BaseWizard {
  AddLearnersStepsEnum = AddLearnersStepsEnum; // Make accessible in template
  @ViewChild(FormActionsComponent) formActions: FormActionsComponent;
  @Output() imported = new EventEmitter<Learner[]>();

  organizations$ = this.organizationsService.list$;
  wizard: AddLearnersWizard = {
    currentStep: AddLearnersStepsEnum.Add,
    steps: [
      {
        label: this.translateService.instant('add-learners-wizard.input-import-learners')
      },
      {
        label: this.translateService.instant('add-learners-wizard.confirm-import-learners')
      },
      {
        label: this.translateService.instant('add-learners-wizard.complete-import-learners')
      }
    ],
    learners: [],
    organizationIds: [],
    imported: [],
    running: false,
    errors: []
  };

  constructor(
    private translateService: TranslateService,
    private learnersService: LearnersService,
    private organizationsService: OrganizationsService,
    public sidebarToggle: SidebarToggleComponent
  ) {
    super();
  }

  onLearnersAdded(learners: IndexedLearnerAttributes[]) {
    const { organizationIds } = this.wizard;
    const importLearners = learners.map((learner) => {
      return { ...learner, organizationIds };
    });
    // add-learners-step returns the entire set
    this.wizard.learners = [...importLearners];
  }

  assignTraining() {
    this.imported.emit(this.wizard.imported);
  }

  editErroredLearners() {
    this.wizard.learners = this.wizard.errors.map((error, index) => {
      return { index, ...error.detail.invitee };
    });
    this.wizard.currentStep--;
  }

  importLearners() {
    this.wizard.running = true;
    this.wizard.errors = [];
    this.wizard.currentStep++;

    const { organizationIds } = this.wizard;
    const importErrors = [];
    const items = this.wizard.learners.map((learner) => {
      delete learner.index;
      delete learner.missingContactInformation;

      const invitee = { ...learner, organizationIds };
      return this.learnersService.create({ invitee });
    });

    of(...items)
      .pipe(
        map((obs$) =>
          obs$.pipe(
            catchError((errors) => {
              // Catch errors error so the stream continues, subscribe stops after an error is encountered
              importErrors.push(...errors);
              return EMPTY;
            })
          )
        ),
        concatAll()
      )
      .subscribe(
        (learner) => {
          if (learner) {
            this.wizard.imported.push(learner);
          }
        },
        noop,
        () => {
          // dump errors all at once to avoid wacky transitions/redraw
          this.wizard.errors = importErrors;
          this.wizard.running = false;
        }
      );
  }
}
